import classNames from 'classnames';
import * as React from 'react';
import { ConfigContext } from '../config-provider';
import SizeContext from '../config-provider/SizeContext';
import useBuriedPoint from '../_util/hooks/useBuriedPoint';
import SkeletonAvatar from './Avatar';
import SkeletonButton from './Button';
import SkeletonNode from './Node';
import Element from './Element';
import SkeletonImage from './Image';
import SkeletonInput from './Input';
import Paragraph from './Paragraph';
import Title from './Title';
function getComponentProps(prop) {
    if (prop && typeof prop === 'object') {
        return prop;
    }
    return {};
}
function getAvatarBasicProps(hasTitle, hasParagraph, size) {
    if (hasTitle && !hasParagraph) {
        // Square avatar
        return { size: size, shape: 'square' };
    }
    return { size: size, shape: 'circle' };
}
function getTitleBasicProps(hasAvatar, hasParagraph) {
    if (!hasAvatar && hasParagraph) {
        return { width: '38%' };
    }
    if (hasAvatar && hasParagraph) {
        return { width: '50%' };
    }
    return {};
}
function getParagraphBasicProps(hasAvatar, hasTitle) {
    const basicProps = {};
    // Width
    if (!hasAvatar || !hasTitle) {
        basicProps.width = '61%';
    }
    // Rows
    if (!hasAvatar && hasTitle) {
        basicProps.rows = 3;
    }
    else {
        basicProps.rows = 2;
    }
    return basicProps;
}
const Skeleton = (props) => {
    const { prefixCls: customizePrefixCls, loading, className, style, children, avatar, title, paragraph, active, round, size: customizeSize, } = props;
    useBuriedPoint('Skeleton');
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('skeleton', customizePrefixCls);
    const size = React.useContext(SizeContext);
    let mergedSize = customizeSize || size;
    switch (mergedSize) {
        case 'middle':
            mergedSize = undefined;
            break;
        default:
            mergedSize = mergedSize;
            break;
    }
    if (loading || !('loading' in props)) {
        const hasAvatar = !!avatar;
        const hasTitle = !!title;
        const hasParagraph = !!paragraph;
        // Avatar
        let avatarNode;
        if (hasAvatar) {
            const avatarProps = Object.assign(Object.assign({ prefixCls: `${prefixCls}-avatar` }, getAvatarBasicProps(hasTitle, hasParagraph, mergedSize)), getComponentProps(avatar));
            // We direct use SkeletonElement as avatar in skeleton internal.
            avatarNode = (React.createElement("div", { className: `${prefixCls}-header` },
                React.createElement(Element, Object.assign({}, avatarProps))));
        }
        let contentNode;
        if (hasTitle || hasParagraph) {
            // Title
            let $title;
            if (hasTitle) {
                const titleProps = Object.assign(Object.assign({ prefixCls: `${prefixCls}-title` }, getTitleBasicProps(hasAvatar, hasParagraph)), getComponentProps(title));
                $title = React.createElement(Title, Object.assign({}, titleProps));
            }
            // Paragraph
            let paragraphNode;
            if (hasParagraph) {
                const paragraphProps = Object.assign(Object.assign({ prefixCls: `${prefixCls}-paragraph` }, getParagraphBasicProps(hasAvatar, hasTitle)), getComponentProps(paragraph));
                paragraphNode = React.createElement(Paragraph, Object.assign({}, paragraphProps));
            }
            contentNode = (React.createElement("div", { className: `${prefixCls}-content` },
                $title,
                paragraphNode));
        }
        const cls = classNames(prefixCls, {
            [`${prefixCls}-with-avatar`]: hasAvatar,
            [`${prefixCls}-active`]: active,
            [`${prefixCls}-rtl`]: direction === 'rtl',
            [`${prefixCls}-round`]: round,
            [`${prefixCls}-${mergedSize}`]: mergedSize,
        }, className);
        return (React.createElement("div", { className: cls, style: style },
            avatarNode,
            contentNode));
    }
    return typeof children !== 'undefined' ? children : null;
};
Skeleton.defaultProps = {
    avatar: false,
    title: true,
    paragraph: true,
};
Skeleton.Button = SkeletonButton;
Skeleton.Avatar = SkeletonAvatar;
Skeleton.Input = SkeletonInput;
Skeleton.Image = SkeletonImage;
Skeleton.Node = SkeletonNode;
export default Skeleton;
